import React, { useRef, useEffect, useReducer, useState } from 'react';
import liff from '@line/liff';
import { useHistory, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Form, TextField, Box, Typography, Grid, Button } from '@curves/ui-base';
import { localUser, isEmpty } from '@Util/utils';
import useAuthApi from '@Apis/useAuthApi';

import LoginLogo from '@Images/logo-white-2026.svg';
import MainFooter from '@Local/Components/Footer/MainFooter';
import SportsEquipmentLoading from "../../Components/loading/SportsEquipmentLoading";
import { generateTokenAuth } from "@Util/apiCryto";
import { MEMBER_SITE_LOGON_TYPE } from "@Constants/";
import { LOGIN_ROOT_ROUTER } from "@Router/routerPath";
import { useLiff } from "../../Providers/LiffProvider";
import { reduce, INIT, EVENTS, EFFECTS } from "./loginStateMachine";


// LIFF 登入外殼元件：串接 loginStateMachine 純 reducer 與各項外部副作用（API、liff SDK、UI state）。
const LiffLoginPage = () => {
    const history = useHistory();
    const { search } = useLocation();
    // LIFF init 已收斂至根層 LiffProvider，本頁不再自行 init，改由 context 取狀態
    const { isReady, initError, officeAccountUrl: liffOfficeAccountUrl, officeAccountChatUrl: liffOfficeAccountChatUrl } = useLiff();
    const [logonType, setLogonType] = useState(MEMBER_SITE_LOGON_TYPE.MEMBER);
    const [clubName, setClubName] = useState('');
    const [liffProfile, setLiffProfile] = useState({});
    const [officeAccountUrl, setOfficeAccountUrl] = useState('');
    const [officeAccountChatUrl, setOfficeAccountChatUrl] = useState('');
    const [isBound, setIsBound] = useState(false);
    const [showUnAuthBox, setShowUnAuthBox] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { enqueueSnackbar: _snackbar } = useSnackbar();
    const {
        postLiffAuthApi, postLiffLoginApi, postLiffLoginEmployeeApi,
        getLogoutApi, getCheckLocalDataApi, getLocalMeDataApi, getLocalMeUiRoleApi
    } = useAuthApi();
    const memberNoRef = useRef(null);
    const captchaRef = useRef(null);

    /**
     * 登入流程顯性狀態機。
     * useReducer 只吃單一 action 物件，故以 { event, payload } 包裝再轉呼叫純函式 reduce。
     */
    const [state, rawDispatch] = useReducer(
        (prevState, action) => reduce(prevState, action.event, action.payload),
        INIT,
    );
    const dispatchEvent = (event, payload = {}) => rawDispatch({ event, payload });

    // StrictMode 下 effect 會 double-invoke，此 ref 記錄「已跑過的 effect+stage」，確保有外部副作用的 effect 只跑一次。
    const ranEffectRef = useRef({});

    // 冪等守衛 helper：以 key 記錄「已跑過」，同 key 第二次呼叫為 no-op（收斂各 effect case 重複的守衛樣板）。
    const runOnce = (key, fn) => {
        if (ranEffectRef.current[key]) return;
        ranEffectRef.current[key] = true;
        fn();
    };

    // 以下 ref 為外殼跨 effect 傳遞資料用：reducer 是純函式、不轉存 payload，故由外殼暫存。
    const sessionMeRef = useRef(null);       // SESSION_HAS_LOGIN 的 me，供 LOGIN_ACTION 導頁
    const liffProfileRef = useRef(null);     // friend 分支的 LIFF profile，供 LIFF_AUTH 取 userId
    const bindAuthResultRef = useRef(null);  // BIND_AUTH 後端 result，供 LOGIN_ACTION / SHOW_BIND_FORM
    const bindFormResultRef = useRef(null);  // BIND_FORM 送出後端 result，供 LOGIN_ACTION / SHOW_LOGIN_ERROR
    const errorMessageRef = useRef('');      // 錯誤訊息，供 SHOW_ERROR 統一顯示

    /**
     * 登入流程診斷 log：統一以 [stage] 前綴輸出各落點與 detail，供真機比對 stage 序列。
     * 預設關閉，不影響正式站 console；需除錯時於裝置 console 執行 localStorage.DEBUG_LIFF = '1' 後重整即開啟。
     */
    const logStage = (name, detail) => {
        // localStorage 存取以 try 包覆，避免隱私模式等環境存取拋錯影響登入流程
        try {
            if (localStorage.getItem('DEBUG_LIFF') !== '1') return;
        } catch (e) {
            return;
        }
        if (detail === undefined) {
            console.log('[stage]', name);
        } else {
            console.log('[stage]', name, detail);
        }
    };

    const handleLoginApi = (e) => {
        e.preventDefault();
        e.stopPropagation();
        getResult();
    };

    // 依 logonType 分派：會員填會員編號，教練填綁定驗證碼，各自呼叫對應的登入 API
    const getResult = () => {
        if (logonType === MEMBER_SITE_LOGON_TYPE.MEMBER) {
            if (!memberNoRef.current.isError()) {
                const memberNo = memberNoRef.current.getResult();
                doLiffLoginApi(memberNo);
            }
        } else if (logonType === MEMBER_SITE_LOGON_TYPE.EMPLOYEE) {
            if (!captchaRef.current.isError()) {
                const captcha = captchaRef.current.getResult();
                doLiffLoginEmployeeApi(captcha);
            }
        }

    };

    // 後端綁定驗證 API 呼叫；結果經參數化 handler（onResult）交 reducer 分類。
    const doLiffAuthApi = async (tokenKey, userId, onResult) => {
        // 送出後端綁定驗證請求
        logStage('BIND_AUTH', { hasTokenKey: !isEmpty(tokenKey), userId });
        await postLiffAuthApi(
            { tokenKey, lineUserID: userId },
            {
                callbackfn: (oriData, apiData) => {
                    const { data } = apiData;
                    if (data.success) {
                        onResult(data.result);
                    }
                },
            },
        );
    };

    /**
     * BIND_AUTH 結果 handler：分類前先處理 sendBindingMessage（await）與 empty-result，
     * 再把 result 原封 dispatch 交 reducer 分類。
     */
    const handleBindAuthResult = async (result) => {
        if (isEmpty(result)) {
            localUser.setToken(null);
            return;
        }
        if (result.isSendBindingMessage) {
            await sendBindingMessage(result);
        }
        bindAuthResultRef.current = result;
        dispatchEvent(EVENTS.BIND_AUTH_RESULT, { result });
    };

    // 傳送綁定成功訊息給使用者；失敗只記錄不拋出，不應該讓這個附加動作卡住整個登入流程
    const sendBindingMessage = async (result) => {
        try {
            if (!liff.isInClient()) return;

            const { logonType = 0 } = result;
            const prefix = logonType === MEMBER_SITE_LOGON_TYPE.MEMBER ? '會員' : '教練';
            const text = `${prefix}綁定成功`;

            await liff.sendMessages([{ type: 'text', text }]);
        } catch (err) {
            console.error('LIFF 發送綁定成功訊息失敗', err);
        }
    };

    // doLiffLoginApi / doLiffLoginEmployeeApi 共用的送出結果 callback
    const doLoginCallback = (oriData, apiData) => {
        const { data } = apiData;
        if (data.success) {
            // result 原封交 reducer 分類（sign-out / unauth / login-success / needs-form）
            bindFormResultRef.current = data.result;
            dispatchEvent(EVENTS.BIND_FORM_SUBMITTED, { result: data.result });
        }
    };

    /**
     * 送出綁定請求前即時取 ID token（不快取）；
     * 外部瀏覽器情境 getIDToken() 可能回 null，需明確處理不可送空值讓後端報錯
     */
    const getFreshIdToken = () => {
        const idToken = liff.getIDToken();
        if (isEmpty(idToken)) {
            _snackbar('無法取得 LINE 登入驗證，請重新整理後再登入', { variant: 'error' });
            return null;
        }
        return idToken;
    };

    // 會員登入：以會員編號送出綁定
    const doLiffLoginApi = async (memberNo) => {
        const idToken = getFreshIdToken();
        if (!idToken) return;

        const tokenKey = localUser.getToken();
        await postLiffLoginApi(
            {
                tokenKey,
                memberNo,
                idToken,
                lineUserID: liffProfile.userId
            },
            {
                callbackfn: doLoginCallback,
            },
        );
    };

    // 教練登入：驗證碼需先雜湊（generateTokenAuth）才送出
    const doLiffLoginEmployeeApi = async (captcha) => {
        const idToken = getFreshIdToken();
        if (!idToken) return;

        const { hmacSecurity, hmacKey } = generateTokenAuth(captcha);
        const tokenKey = localUser.getToken();
        await postLiffLoginEmployeeApi(
            {
                tokenKey,
                captcha: hmacSecurity, hmacKey,
                idToken,
                lineUserID: liffProfile.userId
            },
            {
                callbackfn: doLoginCallback,
            }
        )
    }

    // 讀取 PrivateRoute 導來的 ?redirect= 目標；沒有就回首頁
    const getRedirectUrl = () => {
        const params = new URLSearchParams(search);
        const redirectParam = params.get('redirect');

        if (redirectParam) {
            return decodeURIComponent(redirectParam);
        }

        return '/';
    };


    // reducer 判定 DONE 後的最終落地：寫入登入態，再導頁；資料不完整則視為驗證失敗並登出
    const loginAction = async (result) => {
        const { displayNo = '', displayName = '' } = result;
        if (displayNo !== '' && displayName !== '') {
            localUser.set(result);

            getLocalMeUiRoleApi().then((resp) => {
                if (resp) {
                    localUser.setUiRole(resp);
                }

                // 登入成功後導向原始頁面或預設頁面
                const redirectUrl = getRedirectUrl();
                history.push(redirectUrl);
            });
        } else {
            _snackbar('驗證錯誤，請通知教練協助排解', { variant: 'error' });
            await getLogoutApi(() => {
            });
        }
    };

    const logoutLiff = () => {
        if (liff && liff.isLoggedIn()) {
            /**
             * 比照專案正牌登出流程 getLogoutApi（見 useAuthApi.jsx）：
             * LIFF 登出 → 清前端登入狀態 → 導回登入頁 → 硬重置（清乾淨上一帳號的 in-memory / Redux 狀態）
             */
            liff.logout();
            localUser.logout();
            history.push(LOGIN_ROOT_ROUTER);
            window.location.reload();
        }
    }

    // 「重試」／「再次驗證」：dispatch RETRY 交狀態機（轉 LIFF_LOGIN + RESET_UI + secondary START_LIFF_FLOW，UI 重設在 RESET_UI）。
    const handleReloadLiff = () => {
        dispatchEvent(EVENTS.RETRY);
    }

    /**
     * 啟動 LIFF 流程（primary EFFECT_START_LIFF_FLOW 與 RETRY 的 secondaryEffect 共用）。
     * isLoggedIn 閘門本身無副作用；其觸發的外部副作用由各 effect 分支以 ranEffectRef 守衛只跑一次。
     */
    const startLiffFlow = () => {
        logStage('LIFF_LOGIN', { reason: 'START_LIFF_FLOW' });
        // 已登入 → 進 friendship 查詢；未登入 → 走外部登入導轉。決策交 reducer。
        if (liff.isLoggedIn()) {
            dispatchEvent(EVENTS.LIFF_ALREADY_LOGGED_IN);
        } else {
            logStage('LIFF_LOGIN', { reason: 'LIFF_LOGIN_REQUIRED' });
            dispatchEvent(EVENTS.LIFF_LOGIN_REQUIRED);
        }
    };

    // 查本站登入態並回饋 reducer：已登入 → SESSION_HAS_LOGIN（導頁）；未登入 → SESSION_NO_LOGIN（與 LIFF_READY 合流）。
    const runCheckSession = async () => {
        let resp = await getCheckLocalDataApi();

        if (resp) {
            // 本站已登入，直接導頁（不需 LIFF）
            logStage('SESSION_CHECK', { branch: 'SESSION_HAS_LOGIN' });
            const me = await getLocalMeDataApi();
            sessionMeRef.current = me;
            dispatchEvent(EVENTS.SESSION_HAS_LOGIN, { me });
        } else {
            // 本站未登入，交 reducer 與 LIFF_READY 合流
            logStage('SESSION_CHECK', { branch: 'SESSION_NO_LOGIN', liffReady: isReady });
            dispatchEvent(EVENTS.SESSION_NO_LOGIN);
        }
    };

    // DONE 導頁：依哪個 ref 有值決定用哪份 result（bind-form 送出 > bind-auth 成功 > session 已登入）。
    const runLoginAction = () => {
        if (bindFormResultRef.current) {
            /**
             * bind-form 送出成功路徑：須排在 bindAuthResultRef 之前——
             * needs-binding 流程中 bindAuthResultRef.current 亦已設值，順序錯會誤取舊 auth result。
             */
            const result = bindFormResultRef.current;
            logStage('DONE', { source: 'BIND_FORM_SUBMITTED' });
            loginAction(result);
        } else if (bindAuthResultRef.current) {
            // bind-auth 自動驗證成功路徑：DONE{source:BIND_AUTH}
            const result = bindAuthResultRef.current;
            const { displayNo = '' } = result;
            logStage('DONE', { source: 'BIND_AUTH', displayNo });
            loginAction(result);
        } else {
            // session 已登入路徑：以 SESSION_HAS_LOGIN 暫存的 me 導頁
            const me = sessionMeRef.current;
            me && loginAction(me);
        }
    };

    // 查好友並分支：已加好友 → 取 profile 進 auth；未加好友 → 顯示官方帳號；例外 → 顯示錯誤。結果 dispatch 回 reducer。
    const runGetFriendship = () => {
        logStage('FRIENDSHIP', { reason: 'LIFF_ALREADY_LOGGED_IN' });
        (async () => {
            try {
                const friendship = await liff.getFriendship();

                // getFriendship 成功後立刻收 spinner，friend/not-friend 兩路都跑。
                setIsBound(true);

                if (!friendship.friendFlag) {
                    // 未加好友，顯示加好友（官方帳號）
                    logStage('BIND_FORM', { source: 'FRIENDSHIP_NOT_FRIEND' });
                    setOfficeAccountUrl(liffOfficeAccountUrl);
                    setOfficeAccountChatUrl(liffOfficeAccountChatUrl);
                    dispatchEvent(EVENTS.FRIENDSHIP_NOT_FRIEND);
                } else {
                    // 已加好友，取 profile 後進 auth
                    logStage('FRIENDSHIP', { reason: 'FRIENDSHIP_FRIEND' });
                    // profile 僅供 UI 顯示與取 userId 送後端；idToken 不快取，於送出綁定當下即時取（見 doLiffLoginApi）
                    const profile = await liff.getProfile();
                    setLiffProfile(profile);
                    liffProfileRef.current = profile;
                    dispatchEvent(EVENTS.FRIENDSHIP_FRIEND, { profile });
                }
            } catch (err) {
                // friendship / auth 例外：不靜默卡死，解除 loading 並顯示可感知錯誤（v2.23.2+ 可讀 cause 供診斷）
                logStage('ERROR', { reason: 'FRIENDSHIP_ERROR' });
                console.error('LIFF friendship / auth 流程錯誤', err, err && err.cause);
                errorMessageRef.current = '登入驗證失敗，請重新整理或稍後再試';
                dispatchEvent(EVENTS.FRIENDSHIP_ERROR, { error: err });
            }
        })();
    };

    // 進入點（掛載時一次）：送出 SESSION_CHECK_REQUESTED，由狀態機接手驅動後續流程。
    useEffect(
        () => {
            document.body.className = 'login-page';

            logStage('INIT');
            dispatchEvent(EVENTS.SESSION_CHECK_REQUESTED);

            return () => {
                document.body.className = '';
            };
        },
        // eslint-disable-next-line
        [],
    );

    // LiffProvider init 完成（isReady 變 true）→ 送 LIFF_READY，合流由 reducer 判定。
    useEffect(
        () => {
            if (isReady) {
                dispatchEvent(EVENTS.LIFF_READY);
            }
        },
        // eslint-disable-next-line
        [isReady],
    );

    /**
     * 副作用執行器：監看 reducer 宣告的 effect，執行對應外殼。
     * 有外部副作用者以 ranEffectRef 守衛「同一 effect+stage 只跑一次」（StrictMode 冪等）。
     */
    useEffect(
        () => {
            const { stage, effect, secondaryEffect } = state;
            // 無 primary 也無 secondary 才略過（避免漏跑 secondaryEffect）
            if (!effect && !secondaryEffect) return;

            const guardKey = `${stage}:${effect}`;

            switch (effect) {
                case EFFECTS.CHECK_SESSION:
                    runOnce(guardKey, runCheckSession);
                    break;
                case EFFECTS.LOGIN_ACTION:
                    runOnce(guardKey, runLoginAction);
                    break;
                case EFFECTS.START_LIFF_FLOW: {
                    startLiffFlow();
                    break;
                }
                case EFFECTS.LIFF_LOGIN:
                    // 外部瀏覽器未登入時的登入導轉（導轉外部、守衛只跑一次）。
                    runOnce(guardKey, () => {
                        // redirectUri 帶本頁（?t= token 已由 AuthPage 存於 localStorage，可跨 redirect 保留）
                        liff.login({ redirectUri: window.location.href });
                    });
                    break;
                case EFFECTS.GET_FRIENDSHIP:
                    // friendship 查詢＋分支決策，結果 dispatch 回 reducer（friend / not-friend / error）。守衛只跑一次。
                    runOnce(guardKey, runGetFriendship);
                    break;
                case EFFECTS.LIFF_AUTH:
                    // friend 分支後端 auth（外部 API，守衛只跑一次）。
                    runOnce(guardKey, () => {
                        // 無 profile 不呼叫防呆（正常路徑一定有）；結果交 handleBindAuthResult → reducer 分類
                        const tokenKey = localUser.getToken();
                        const profile = liffProfileRef.current;
                        profile && doLiffAuthApi(tokenKey, profile.userId, handleBindAuthResult);
                    });
                    break;
                case EFFECTS.LOGOUT_LIFF:
                    // BIND_AUTH sign-out：auth 路徑無 snackbar，僅印落點後登出 LIFF。
                    runOnce(guardKey, () => {
                        logStage('SIGNED_OUT', { hasSignOutMessage: false });
                        logoutLiff();
                    });
                    break;
                case EFFECTS.SHOW_SIGNOUT_SNACKBAR:
                    // BIND_FORM 送出回 sign-out：login 路徑帶 snackbar；logout 由 secondaryEffect 執行。
                    runOnce(guardKey, () => {
                        logStage('SIGNED_OUT', { hasSignOutMessage: true });
                        _snackbar('LINE綁定驗證錯誤，請重新取得登入驗證', { variant: 'error' });
                    });
                    break;
                case EFFECTS.SHOW_LOGIN_ERROR:
                    // BIND_FORM 送出回 success 卻無 displayNo/displayName（防禦邊界，實務近乎不可達）：鏡射 loginAction else。
                    runOnce(guardKey, () => {
                        logStage('BIND_FORM', { source: 'BIND_FORM_SUBMITTED', reason: 'LOGIN_ERROR' });
                        _snackbar('驗證錯誤，請通知教練協助排解', { variant: 'error' });
                        getLogoutApi(() => {});
                    });
                    break;
                case EFFECTS.SHOW_UNAUTH:
                    // BIND_AUTH 回 isRedirectToUnAuthUri，顯示無法綁定驗證區塊。
                    runOnce(guardKey, () => {
                        logStage('UNAUTH');
                        setShowUnAuthBox(true);
                    });
                    break;
                case EFFECTS.SHOW_BIND_FORM:
                    // BIND_AUTH 回未綁定（有 clubName 無 displayNo），顯示綁定表單。
                    runOnce(guardKey, () => {
                        const result = bindAuthResultRef.current || {};
                        const { logonType = 0, clubName = '' } = result;
                        logStage('BIND_FORM', { source: 'BIND_AUTH', clubName, logonType });
                        setClubName(clubName);
                        setLogonType(logonType);
                    });
                    break;
                case EFFECTS.RESET_UI: {
                    /**
                     * RETRY 重設 UI：印 log、重設四個 UI state，並清空 ranEffectRef 守衛讓重試能重跑 forward effect。
                     * 重試從 LIFF_LOGIN 起、不回 INIT/SESSION_CHECK，清全部安全；本 case 先跑、secondary START_LIFF_FLOW 後跑。
                     */
                    logStage('LIFF_LOGIN', { reason: 'RETRY' });
                    setIsBound(false);
                    setOfficeAccountUrl('');
                    setOfficeAccountChatUrl('');
                    setErrorMessage('');
                    ranEffectRef.current = {};
                    break;
                }
                case EFFECTS.SHOW_ERROR:
                    // ERROR 態統一顯示（FRIENDSHIP_ERROR / LIFF_INIT_ERROR / FLOW_ERROR 匯流至此），訊息由 errorMessageRef 傳遞。
                    runOnce(guardKey, () => {
                        setIsBound(true);
                        setErrorMessage(errorMessageRef.current);
                    });
                    break;
                default:
                    break;
            }

            /**
             * 兩段式轉移（sign-out：先 snackbar 再 logout；RETRY：先 RESET_UI 再重啟）的第二段副作用。
             * 守衛 key 用 secondary 命名空間，與 primary 的 guardKey 不撞，確保只跑一次。
             */
            if (secondaryEffect === EFFECTS.LOGOUT_LIFF) {
                const secKey = `${stage}:secondary:${secondaryEffect}`;
                if (!ranEffectRef.current[secKey]) {
                    ranEffectRef.current[secKey] = true;
                    // 純副作用，不重複印 SIGNED_OUT log（已由 SHOW_SIGNOUT_SNACKBAR 印一次）
                    logoutLiff();
                }
            } else if (secondaryEffect === EFFECTS.START_LIFF_FLOW) {
                const secKey = `${stage}:secondary:${secondaryEffect}`;
                if (!ranEffectRef.current[secKey]) {
                    ranEffectRef.current[secKey] = true;
                    startLiffFlow();
                }
            }
        },
        // eslint-disable-next-line
        [state],
    );

    // LIFF init 失敗（來自 LiffProvider）：解除登入中 loading 並顯示可感知錯誤，不靜默卡死
    useEffect(
        () => {
            if (!isEmpty(initError)) {
                /**
                 * init 失敗時 isReady 永不為真、LIFF_READY 不發，合流卡在 SESSION_CHECK；
                 * 訊息以 ref 傳遞，UI 更新統一由 EFFECT_SHOW_ERROR 執行。
                 */
                logStage('ERROR', { reason: 'LIFF_INIT_ERROR' });
                errorMessageRef.current = initError;
                dispatchEvent(EVENTS.LIFF_INIT_ERROR);
            }
        },
        // eslint-disable-next-line
        [initError],
    );

    return (
        <React.Fragment>
            <Box className="login-body">
                <Box className="container">
                    <Box className="logo-wrap">
                        <img src={LoginLogo} className="login-logo" alt="curves" />
                    </Box>
                    {!isBound && <SportsEquipmentLoading size={1} text={'登入中'} />}
                    {!isEmpty(errorMessage) && (
                        <Box className="login-error-wrap">
                            <Typography className="text-center" variant="h5" color="white">
                                {errorMessage}
                            </Typography>
                            <Box mt={2} className="text-center">
                                <Button variant="outlined" color="success" onClick={handleReloadLiff}>
                                    重試
                                </Button>
                            </Box>
                        </Box>
                    )}
                    {isEmpty(errorMessage) && !isEmpty(officeAccountUrl) && (
                        <Box className="office-account-wrap">
                            <h4>請先加入官方帳號好友</h4>
                            <a href={officeAccountUrl} target="_blank" rel="noopener noreferrer">
                                <img src="https://scdn.line-apps.com/n/line_add_friends/btn/zh-Hant.png" alt="加入好友" height="36" border="0" />
                            </a>
                            <br /><br />
                            <Button variant="outlined" color="success" onClick={handleReloadLiff}>
                                再次驗證
                            </Button>
                            {!isEmpty(officeAccountChatUrl) && (
                                <Box mt={2}>
                                    <Typography variant="caption" color="white">
                                        曾加入但無法綁定？您可能曾封鎖此官方帳號，
                                        <a href={officeAccountChatUrl} target="_blank" rel="noopener noreferrer">
                                            點此開啟聊天視窗
                                        </a>
                                        ，從右上角選單解除封鎖後再試。
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                    {isEmpty(errorMessage) && !showUnAuthBox && (
                        <Form className="login-form" onSubmit={handleLoginApi}>
                            <Grid container spacing={3}>
                                {!isEmpty(liffProfile) && (
                                    <React.Fragment>
                                        <Grid item xs={12}>
                                            <img src={liffProfile.pictureUrl} className="line-user-picture" alt="LINE 大頭照" />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography className="line-user-disply" variant="body1">{liffProfile.displayName}</Typography>
                                        </Grid>
                                    </React.Fragment>
                                )}
                                {!isEmpty(clubName) && (
                                    <React.Fragment>
                                        {logonType === MEMBER_SITE_LOGON_TYPE.MEMBER && (
                                            <Grid item xs={12}>
                                                <TextField ref={memberNoRef} label={`${clubName} 會員編號`} fullWidth />
                                            </Grid>
                                        )}
                                        {logonType === MEMBER_SITE_LOGON_TYPE.EMPLOYEE && (
                                            <Grid item xs={12}>
                                                <TextField ref={captchaRef} label={`${clubName} 教練綁定驗證碼`} fullWidth />
                                            </Grid>
                                        )}

                                        <Grid item xs={12} className="login-btn-confirm">
                                            <Button variant="outlined" type="submit">
                                                綁定驗證
                                            </Button>
                                        </Grid>
                                    </React.Fragment>
                                )}
                            </Grid>
                        </Form>)
                    }
                    {showUnAuthBox && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography className="text-center" variant="h3" color="white">
                                    無法綁定驗證<br />請通知教練協助排解
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </Box>
            <MainFooter />
        </React.Fragment>
    );
};

export default LiffLoginPage;
